import React, { FC, useMemo, useReducer } from 'react';
import { useMutation } from '@apollo/client';
import { DialogProps } from '@radix-ui/react-dialog';
import * as Sentry from '@sentry/react';

import { Modal, ModalContent, ModalContentProps } from '@epoch-ui/components/Modal/modal';
import { ToasterVariant, useToaster } from 'hooks/useToaster';
import { EventType } from 'types/event.types';
import { GuestStatus } from 'types/guest.types';
import {
  UPDATE_PLUS_ONE_RSVP_STATUS_MUTATION,
  UpdatePlusOneRsvpStatusResponse,
  UpdatePlusOneRsvpStatusVariables,
  UpdatePlusOneRsvpStatusError
} from 'graphql/mutations/rsvp_control/plus_one/update-plus-one-rsvp-status';
import { QuestionResponse } from 'types/question-response.types';

import { useGuestSideSheetContext } from '../../../guestSideSheetContext';

import {
  PlusOneRsvpControlModalContextType,
  PlusOneRsvpControlModalContext
} from './plusOneRsvpControlContext';
import {
  SetPlusOneWaitlistAdjustmentOptionContent,
  SubmitQuestionnaireContent
} from './ModalContent';
import { ConfirmOwnerQuestionnaireContent } from './ModalContent/ConfirmOwnerQuestionnaireContent';
import { SetUpdateNotificationsForPlusOneContent } from './ModalContent/SetUpdateNotificationsForPlusOneContent';
import { ConfirmChangeToWaitlistedContent } from './ModalContent/ConfirmChangeToWaitlistedContent';

enum PlusOneRsvpControlModalStep {
  SetPlusOneWaitlistAdjustmentOption = 'SetPlusOneWaitlistAdjustmentOption',
  SetUpdateNotifications = 'SetUpdateNotifications',
  ConfirmChangeToWaitlisted = 'ConfirmChangeToWaitlisted',
  ConfirmOwnerQuestionnaire = 'ConfirmOwnerQuestionnaire',
  SubmitOwnerQuestionnaire = 'SubmitOwnerQuestionnaire',
  SubmitPlusOneQuestionnaire = 'SubmitPlusOneQuestionnaire',
  END = 'END',
  INVALID = 'INVALID'
}

const modalContentPropsForStep = (step: PlusOneRsvpControlModalStep): ModalContentProps => {
  const className = 'tw-gap-5';
  switch (step) {
    case PlusOneRsvpControlModalStep.SetPlusOneWaitlistAdjustmentOption:
      return { className, size: 'md' };
    case PlusOneRsvpControlModalStep.ConfirmOwnerQuestionnaire:
      return { className, size: 'sm' };
    case PlusOneRsvpControlModalStep.SubmitOwnerQuestionnaire:
      return { className, size: 'lg' };
    case PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire:
      return { className, size: 'lg' };
    case PlusOneRsvpControlModalStep.SetUpdateNotifications:
      return { className, size: 'md' };
    case PlusOneRsvpControlModalStep.ConfirmChangeToWaitlisted:
      return { className, size: 'sm' };
    default:
      return { className };
  }
};

interface GetNextPlusOneRsvpControlModalStepProps {
  currentStep: PlusOneRsvpControlModalStep;
  requiresPlusOneQuestionnaireSubmission: boolean;
  requiresOwnerQuestionnaireSubmission: boolean;
}

const getNextPlusOneRsvpControlModalStep = (
  props: GetNextPlusOneRsvpControlModalStepProps
): PlusOneRsvpControlModalStep => {
  const {
    currentStep,
    requiresPlusOneQuestionnaireSubmission,
    requiresOwnerQuestionnaireSubmission
  } = props;
  switch (currentStep) {
    case PlusOneRsvpControlModalStep.SetPlusOneWaitlistAdjustmentOption:
      // Waitlisted -> Going: after adjustment, check owner questionnaire first if needed
      if (requiresOwnerQuestionnaireSubmission) {
        return PlusOneRsvpControlModalStep.ConfirmOwnerQuestionnaire;
      }
      if (requiresPlusOneQuestionnaireSubmission) {
        return PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire;
      }
      return PlusOneRsvpControlModalStep.SetUpdateNotifications;

    case PlusOneRsvpControlModalStep.ConfirmOwnerQuestionnaire:
      return PlusOneRsvpControlModalStep.SubmitOwnerQuestionnaire;

    case PlusOneRsvpControlModalStep.SubmitOwnerQuestionnaire:
      if (requiresPlusOneQuestionnaireSubmission) {
        return PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire;
      }
      return PlusOneRsvpControlModalStep.SetUpdateNotifications;

    case PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire:
      return PlusOneRsvpControlModalStep.SetUpdateNotifications;

    case PlusOneRsvpControlModalStep.SetUpdateNotifications:
      return PlusOneRsvpControlModalStep.END;

    case PlusOneRsvpControlModalStep.ConfirmChangeToWaitlisted:
      return PlusOneRsvpControlModalStep.END;

    default:
      return PlusOneRsvpControlModalStep.INVALID;
  }
};

const getInitialStep = (
  plusOneCurrentStatus: GuestStatus,
  selectedStatus: GuestStatus,
  requiresPlusOneQuestionnaireSubmission: boolean,
  requiresOwnerQuestionnaireSubmission: boolean,
  isEventWaitlisted: boolean
): PlusOneRsvpControlModalStep => {
  switch (plusOneCurrentStatus) {
    case GuestStatus.CONFIRMED:
      // Going -> Waitlisted
      if (selectedStatus === GuestStatus.WAITLISTED && isEventWaitlisted) {
        return PlusOneRsvpControlModalStep.ConfirmChangeToWaitlisted;
      }
      return PlusOneRsvpControlModalStep.INVALID;

    case GuestStatus.WAITLISTED:
      // Waitlisted -> Going
      if (selectedStatus === GuestStatus.CONFIRMED) {
        return PlusOneRsvpControlModalStep.SetPlusOneWaitlistAdjustmentOption;
      }
      return PlusOneRsvpControlModalStep.INVALID;

    case GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY:
      // Incomplete -> Going/Waitlisted
      if ([GuestStatus.CONFIRMED, GuestStatus.WAITLISTED].includes(selectedStatus)) {
        if (requiresOwnerQuestionnaireSubmission) {
          return PlusOneRsvpControlModalStep.ConfirmOwnerQuestionnaire;
        }
        if (requiresPlusOneQuestionnaireSubmission) {
          return PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire;
        }
      }
      return PlusOneRsvpControlModalStep.INVALID;

    case GuestStatus.ACTION_REQUIRED:
      // Pending -> Going (event is waitlisted): show waitlist adjustment option first
      if (selectedStatus === GuestStatus.CONFIRMED && isEventWaitlisted) {
        return PlusOneRsvpControlModalStep.SetPlusOneWaitlistAdjustmentOption;
      }
      // Pending -> Going (event has spaces) or Pending -> Waitlisted
      if ([GuestStatus.CONFIRMED, GuestStatus.WAITLISTED].includes(selectedStatus)) {
        if (requiresOwnerQuestionnaireSubmission) {
          return PlusOneRsvpControlModalStep.ConfirmOwnerQuestionnaire;
        }
        if (requiresPlusOneQuestionnaireSubmission) {
          return PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire;
        }
        return PlusOneRsvpControlModalStep.SetUpdateNotifications;
      }
      return PlusOneRsvpControlModalStep.INVALID;

    default:
      return PlusOneRsvpControlModalStep.INVALID;
  }
};

interface PlusOneRsvpControlModalState {
  // navigation
  currentStepNumber: number;
  currentStep: PlusOneRsvpControlModalStep;
  navigationStack: PlusOneRsvpControlModalStep[];

  // questionnaire
  updatedResponses: QuestionResponse[];
  updatedOwnerResponses: QuestionResponse[];

  // notifications
  sendNotifications: boolean;
}

const subStateForStep = (
  state: PlusOneRsvpControlModalState,
  step: PlusOneRsvpControlModalStep
): Partial<PlusOneRsvpControlModalState> => {
  switch (step) {
    case PlusOneRsvpControlModalStep.SetPlusOneWaitlistAdjustmentOption:
      return {};
    case PlusOneRsvpControlModalStep.ConfirmOwnerQuestionnaire:
      return {};
    case PlusOneRsvpControlModalStep.SubmitOwnerQuestionnaire:
      return { updatedOwnerResponses: state.updatedOwnerResponses };
    case PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire:
      return { updatedResponses: state.updatedResponses };
    case PlusOneRsvpControlModalStep.SetUpdateNotifications:
      return { sendNotifications: state.sendNotifications };
    default:
      return {};
  }
};

enum PlusOneRsvpControlModalStateActionType {
  ProceedToStep = 'ProceedToStep',
  NavigateBack = 'NavigateBack',
  SetUpdatedResponses = 'SetUpdatedResponses',
  SetUpdatedOwnerResponses = 'SetUpdatedOwnerResponses',
  SetNotificationSettings = 'SetNotificationSettings',
  ResetState = 'ResetState'
}

type PlusOneRsvpControlModalStateAction =
  | {
      type: PlusOneRsvpControlModalStateActionType.ProceedToStep;
      step: PlusOneRsvpControlModalStep;
    }
  | {
      type: PlusOneRsvpControlModalStateActionType.NavigateBack;
      initialState: PlusOneRsvpControlModalState;
    }
  | {
      type: PlusOneRsvpControlModalStateActionType.SetUpdatedResponses;
      updatedResponses: QuestionResponse[];
    }
  | {
      type: PlusOneRsvpControlModalStateActionType.SetUpdatedOwnerResponses;
      updatedOwnerResponses: QuestionResponse[];
    }
  | {
      type: PlusOneRsvpControlModalStateActionType.SetNotificationSettings;
      sendNotifications: boolean;
    }
  | {
      type: PlusOneRsvpControlModalStateActionType.ResetState;
      initialState: PlusOneRsvpControlModalState;
    };

const plusOneRsvpControlModalStateReducer = (
  state: PlusOneRsvpControlModalState,
  action: PlusOneRsvpControlModalStateAction
): PlusOneRsvpControlModalState => {
  if (action.type === PlusOneRsvpControlModalStateActionType.ProceedToStep) {
    const { step } = action;
    return {
      ...state,
      navigationStack: [state.currentStep, ...state.navigationStack],
      currentStep: step,
      currentStepNumber: state.currentStepNumber + 1
    };
  } else if (action.type === PlusOneRsvpControlModalStateActionType.NavigateBack) {
    const [previousStep, ...previousSteps] = state.navigationStack;
    const revertSubState = subStateForStep(action.initialState, state.currentStep);
    return {
      ...state,
      navigationStack: previousSteps,
      currentStep: previousStep,
      currentStepNumber: state.currentStepNumber - 1,
      ...revertSubState
    };
  } else if (action.type === PlusOneRsvpControlModalStateActionType.SetUpdatedResponses) {
    return { ...state, updatedResponses: action.updatedResponses };
  } else if (action.type === PlusOneRsvpControlModalStateActionType.SetUpdatedOwnerResponses) {
    return { ...state, updatedOwnerResponses: action.updatedOwnerResponses };
  } else if (action.type === PlusOneRsvpControlModalStateActionType.SetNotificationSettings) {
    return { ...state, sendNotifications: action.sendNotifications };
  } else if (action.type === PlusOneRsvpControlModalStateActionType.ResetState) {
    return action.initialState;
  } else {
    return state;
  }
};

interface PlusOneRsvpControlModalProps extends DialogProps {
  selectedStatus: GuestStatus;
  onRsvpUpdate: () => void;
}

export const PlusOneRsvpControlModal: FC<PlusOneRsvpControlModalProps> = ({
  open,
  onOpenChange,
  selectedStatus,
  onRsvpUpdate
}) => {
  const {
    eventOrSessionId,
    guestData: { guest: ownerGuest, event },
    selectedPlusOneGuest,
    selectedSession
  } = useGuestSideSheetContext();
  const plusOneGuest = selectedPlusOneGuest!;

  // Use session-level waitlist status for multi-session events
  const isEventWaitlisted: boolean =
    event.eventType === EventType.MULTI_SESSIONS && !!selectedSession
      ? !!selectedSession.isEventWaitlisted
      : event.isEventWaitlisted;

  const requiresPlusOneQuestionnaireSubmission =
    event.preeventEnable &&
    event.preEventQuestions.length > 0 &&
    [GuestStatus.CONFIRMED, GuestStatus.WAITLISTED].includes(selectedStatus);

  // Check if owner (owner guest) has incomplete questionnaire and needs to fill it
  const requiresOwnerQuestionnaireSubmission =
    requiresPlusOneQuestionnaireSubmission &&
    ownerGuest.status === GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY;

  const initialStep = getInitialStep(
    plusOneGuest.status,
    selectedStatus,
    requiresPlusOneQuestionnaireSubmission,
    requiresOwnerQuestionnaireSubmission,
    isEventWaitlisted
  );

  const INITIAL_STATE: PlusOneRsvpControlModalState = useMemo(
    () => ({
      // navigation
      currentStepNumber: 1,
      currentStep: initialStep,
      navigationStack: [initialStep],

      // questionnaire
      updatedResponses: plusOneGuest.questionnaireResponses,
      updatedOwnerResponses: ownerGuest.surveyResponses || [],

      // notifications
      sendNotifications: false
    }),
    [
      initialStep,
      plusOneGuest.questionnaireResponses,
      event.preEventQuestions,
      ownerGuest.surveyResponses
    ]
  );

  const [state, dispatch] = useReducer<
    (
      state: PlusOneRsvpControlModalState,
      action: PlusOneRsvpControlModalStateAction
    ) => PlusOneRsvpControlModalState
  >(plusOneRsvpControlModalStateReducer, INITIAL_STATE);

  const { showToast } = useToaster();
  const showErrorToast = (textInput: string): void =>
    showToast(textInput, { variant: ToasterVariant.ERROR });

  const [updatePlusOneRsvpStatus, { loading }] = useMutation<
    UpdatePlusOneRsvpStatusResponse,
    UpdatePlusOneRsvpStatusVariables
  >(UPDATE_PLUS_ONE_RSVP_STATUS_MUTATION, {
    onCompleted: () => {
      showToast(`${plusOneGuest.firstName}'s status has been updated.`);
      handleClose();
      onRsvpUpdate();
    },
    onError: (error) => {
      switch (error.graphQLErrors?.[0]?.message) {
        case UpdatePlusOneRsvpStatusError.PLUS_ONE_GUEST_NOT_FOUND:
          showErrorToast('Plus-one guest not found.');
          break;
        case UpdatePlusOneRsvpStatusError.PLUS_ONES_DISABLED:
          showErrorToast('Plus-one guests are disabled for this event.');
          break;
        case UpdatePlusOneRsvpStatusError.OWNER_GUEST_NOT_FOUND:
          showErrorToast('Owner guest not found.');
          break;
        case UpdatePlusOneRsvpStatusError.OWNER_GUEST_NOT_CONFIRMED:
          showErrorToast('Owner guest must be confirmed first.');
          break;
        case UpdatePlusOneRsvpStatusError.INVALID_STATUS_CHANGE:
          showErrorToast(
            "Plus-one guest status couldn't be updated at this time due to a status conflict. Please try again."
          );
          break;
        case UpdatePlusOneRsvpStatusError.EVENT_IS_OVER:
          showErrorToast(
            "Plus-one guest status couldn't be updated because the event is now over."
          );
          break;
        case UpdatePlusOneRsvpStatusError.CAPACITY_INCLUDES_PLUS_ONES_DISABLED:
          showErrorToast(
            "Plus-one guest status couldn't be updated because capacity includes plus-ones is disabled."
          );
          break;
        default:
          showErrorToast("Plus-one guest status couldn't be updated. Please try again later.");
          Sentry.setContext('PlusOneRsvpControlState', state as any);
          Sentry.captureException(error);
      }
      handleClose();
      onRsvpUpdate();
    }
  });

  const totalStepNumber = useMemo((): number => {
    let stepCount = 0;

    // Going -> Waitlisted: just SetUpdateNotifications (1 step)
    if (
      plusOneGuest.status === GuestStatus.CONFIRMED &&
      selectedStatus === GuestStatus.WAITLISTED
    ) {
      return 1;
    }

    // Waitlisted -> Going: SetPlusOneWaitlistAdjustmentOption + owner questionnaire steps? + plus one questionnaire? + SetUpdateNotifications
    if (
      plusOneGuest.status === GuestStatus.WAITLISTED &&
      selectedStatus === GuestStatus.CONFIRMED
    ) {
      stepCount = 2; // SetPlusOneWaitlistAdjustmentOption + SetUpdateNotifications
      if (requiresOwnerQuestionnaireSubmission) stepCount += 2; // ConfirmOwnerQuestionnaire + SubmitOwnerQuestionnaire
      if (requiresPlusOneQuestionnaireSubmission) stepCount += 1; // SubmitPlusOneQuestionnaire
      return stepCount;
    }

    // Incomplete -> Going/Waitlisted: owner questionnaire steps? + plus one questionnaire? + SetUpdateNotifications
    if (
      plusOneGuest.status === GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY &&
      [GuestStatus.CONFIRMED, GuestStatus.WAITLISTED].includes(selectedStatus)
    ) {
      if (requiresOwnerQuestionnaireSubmission) stepCount += 2; // ConfirmOwnerQuestionnaire + SubmitOwnerQuestionnaire
      if (requiresPlusOneQuestionnaireSubmission) stepCount += 1; // SubmitPlusOneQuestionnaire
      stepCount += 1; // SetUpdateNotifications
      return stepCount;
    }

    // Pending (ACTION_REQUIRED) -> Going/Waitlisted
    if (
      plusOneGuest.status === GuestStatus.ACTION_REQUIRED &&
      [GuestStatus.CONFIRMED, GuestStatus.WAITLISTED].includes(selectedStatus)
    ) {
      // Pending -> Going (event/session is waitlisted): show waitlist adjustment option first
      if (selectedStatus === GuestStatus.CONFIRMED && isEventWaitlisted) stepCount += 1;

      if (requiresOwnerQuestionnaireSubmission) stepCount += 2;
      if (requiresPlusOneQuestionnaireSubmission) stepCount += 1;
      stepCount += 1; // SetUpdateNotifications
      return stepCount;
    }

    // Fallback
    return 1;
  }, [
    plusOneGuest.status,
    selectedStatus,
    requiresPlusOneQuestionnaireSubmission,
    requiresOwnerQuestionnaireSubmission,
    isEventWaitlisted
  ]);

  const handleClose = (): void => {
    dispatch({
      type: PlusOneRsvpControlModalStateActionType.ResetState,
      initialState: INITIAL_STATE
    });
    onOpenChange?.(false);
  };

  const handleBack = (): void => {
    if (state.currentStepNumber === 1) {
      handleClose();
      return;
    }
    dispatch({
      type: PlusOneRsvpControlModalStateActionType.NavigateBack,
      initialState: INITIAL_STATE
    });
  };

  const submitRsvpChange = (): void => {
    const { sendNotifications, updatedResponses, updatedOwnerResponses } = state;

    const toStatusString = (status: GuestStatus): string => {
      return Object.keys(GuestStatus)[Object.values(GuestStatus).indexOf(status)];
    };

    const questionnaireResponses = requiresPlusOneQuestionnaireSubmission ? updatedResponses : [];

    const ownerQuestionnaireResponses = requiresOwnerQuestionnaireSubmission
      ? updatedOwnerResponses
      : [];

    updatePlusOneRsvpStatus({
      variables: {
        input: {
          eventId: eventOrSessionId,
          gid: ownerGuest.gid,
          plusOneId: plusOneGuest.plusOneId,
          status: toStatusString(selectedStatus),
          sendNotifications,
          questionnaireResponses,
          ownerQuestionnaireResponses
        }
      }
    });
  };

  const handleSubmit = (): void => {
    const nextStep = getNextPlusOneRsvpControlModalStep({
      currentStep: state.currentStep,
      requiresPlusOneQuestionnaireSubmission,
      requiresOwnerQuestionnaireSubmission
    });

    if (nextStep === PlusOneRsvpControlModalStep.END) {
      submitRsvpChange();
    } else {
      dispatch({ type: PlusOneRsvpControlModalStateActionType.ProceedToStep, step: nextStep });
    }
  };

  const contextValue: PlusOneRsvpControlModalContextType = useMemo(
    () => ({
      navigation: {
        currentStepNumber: state.currentStepNumber,
        totalStepNumber,
        loading,
        onCancel: handleClose,
        onClickBack: handleBack,
        onClickSubmit: handleSubmit
      },
      ownerGuest,
      plusOneGuest,
      eventOrSessionId,
      event,
      selectedStatus,
      requiresPlusOneQuestionnaireSubmission,
      updatedResponses: state.updatedResponses,
      setUpdatedResponses: (updatedResponses: QuestionResponse[]) =>
        dispatch({
          type: PlusOneRsvpControlModalStateActionType.SetUpdatedResponses,
          updatedResponses
        }),
      requiresOwnerQuestionnaireSubmission,
      updatedOwnerResponses: state.updatedOwnerResponses,
      setUpdatedOwnerResponses: (updatedOwnerResponses: QuestionResponse[]) =>
        dispatch({
          type: PlusOneRsvpControlModalStateActionType.SetUpdatedOwnerResponses,
          updatedOwnerResponses
        }),
      sendNotifications: state.sendNotifications,
      setSendNotifications: (value: boolean) =>
        dispatch({
          type: PlusOneRsvpControlModalStateActionType.SetNotificationSettings,
          sendNotifications: value
        })
    }),
    [
      state,
      totalStepNumber,
      loading,
      ownerGuest,
      plusOneGuest,
      eventOrSessionId,
      event,
      selectedStatus,
      requiresPlusOneQuestionnaireSubmission,
      requiresOwnerQuestionnaireSubmission
    ]
  );

  const renderModalContent = (): React.ReactNode => {
    switch (state.currentStep) {
      case PlusOneRsvpControlModalStep.SetPlusOneWaitlistAdjustmentOption:
        return <SetPlusOneWaitlistAdjustmentOptionContent />;
      case PlusOneRsvpControlModalStep.ConfirmOwnerQuestionnaire:
        return <ConfirmOwnerQuestionnaireContent />;
      case PlusOneRsvpControlModalStep.SubmitOwnerQuestionnaire:
        return <SubmitQuestionnaireContent key="owner" isOwnerQuestionnaire />;
      case PlusOneRsvpControlModalStep.SubmitPlusOneQuestionnaire:
        return <SubmitQuestionnaireContent key="plusOne" isOwnerQuestionnaire={false} />;
      case PlusOneRsvpControlModalStep.SetUpdateNotifications:
        return <SetUpdateNotificationsForPlusOneContent />;
      case PlusOneRsvpControlModalStep.ConfirmChangeToWaitlisted:
        return <ConfirmChangeToWaitlistedContent />;
      default:
        return null;
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent {...modalContentPropsForStep(state.currentStep)}>
        <PlusOneRsvpControlModalContext.Provider value={contextValue}>
          {renderModalContent()}
        </PlusOneRsvpControlModalContext.Provider>
      </ModalContent>
    </Modal>
  );
};
